import { useEffect, useState } from "react";
import { motion, useScroll } from "framer-motion";
import { Button } from "../ui/button";
import { Menu, X, GraduationCap } from "lucide-react";
import ApplyDialog from "./ApplyDialog";
import { COLLEGE } from "../../lib/constants";

const links = [
  { id: "about", label: "About" },
  { id: "programs", label: "Programs" },
  { id: "placements", label: "Placements" },
  { id: "campus", label: "Campus" },
  { id: "admissions", label: "Admissions" },
  { id: "faq", label: "FAQ" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Scroll progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 origin-left z-[60] bg-gradient-to-r from-megha-highlight via-megha-accent to-megha-primary"
        style={{ scaleX: scrollYProgress }}
        data-testid="scroll-progress"
      />

      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "glass shadow-sm border-b border-white/40" : "bg-white"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-megha-primary to-megha-accent flex items-center justify-center text-white shadow-lg shadow-blue-900/20 group-hover:scale-105 transition-transform">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-[15px] md:text-base text-megha-text tracking-tight">
                MIETW
              </div>
              <div className="text-[10px] md:text-[11px] text-slate-500 -mt-0.5 uppercase tracking-[0.18em]">
                Admissions 2026
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`nav-${l.id}`}
                className="text-sm font-semibold text-slate-700 hover:text-megha-primary transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-megha-highlight group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ApplyDialog
              trigger={
                <Button
                  data-testid="btn-apply-now-header"
                  className="hidden sm:inline-flex h-10 rounded-full bg-megha-primary hover:bg-megha-accent text-white font-bold px-5 shadow-md shadow-blue-900/20 transition-all"
                >
                  Apply Now
                </Button>
              }
            />
            <button
              data-testid="btn-mobile-menu"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-3 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </a>
              ))}
              <ApplyDialog
                trigger={
                  <Button
                    data-testid="btn-apply-now-mobile"
                    className="w-full mt-2 h-11 rounded-full bg-megha-primary hover:bg-megha-accent text-white font-bold"
                  >
                    Apply for B.Tech 2026
                  </Button>
                }
              />
              <div className="text-xs text-slate-500 px-3 pt-2">
                Call: {COLLEGE.phones[0]}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
